import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';
import mobileDeviceApi from '../services/mobileDeviceApi';
import InfoModal from '../components/common/InfoModal';
import UnlockConfirmModal from '../components/UnlockConfirmModal';
import {
    Shield,
    SmartphoneNfc,
    Loader2,
    AlertTriangle,
    CheckCircle2,
    Lock,
    Unlock,
    Users,
    Smartphone,
    Activity,
    ArrowRight,
    RefreshCw,
    ShieldAlert,
    KeyRound,
    Layers
} from 'lucide-react';

export default function TenantSecuritySettingsPage({ onNavigate }) {
    const { permissions } = useAuth();
    const [loading, setLoading] = useState(true);
    const [savingMfa, setSavingMfa] = useState(false);
    const [roleSavingId, setRoleSavingId] = useState(null);
    const [policySaving, setPolicySaving] = useState(false);

    // Overview & MFA States
    const [mfaFeatureEnabled, setMfaFeatureEnabled] = useState(false);
    const [mfaEnabled, setMfaEnabled] = useState(false);
    const [overview, setOverview] = useState(null);

    // Modals
    const [isDisableMfaModalOpen, setIsDisableMfaModalOpen] = useState(false);
    const [selectedUserForUnlock, setSelectedUserForUnlock] = useState(null);

    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const canManageSettings = permissions.includes('settings:manage');

    const navigateTo = (pageKey) => {
        if (onNavigate) {
            onNavigate(pageKey);
        } else {
            window.location.hash = `#/${pageKey}`;
        }
    };

    const fetchOverview = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await apiClient('SecuritySettings/overview');
            setMfaFeatureEnabled(Boolean(data.mfaFeatureEnabled));
            setMfaEnabled(Boolean(data.mfaEnabled));
            setOverview(data);
        } catch (err) {
            // Fallback to basic MFA endpoint if overview fails
            try {
                const basic = await apiClient('SecuritySettings/mfa');
                setMfaFeatureEnabled(Boolean(basic.mfaFeatureEnabled));
                setMfaEnabled(Boolean(basic.mfaEnabled));
            } catch (innerErr) {
                setError(innerErr.payload?.message || innerErr.message || 'Güvenlik ayarları alınamadı.');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canManageSettings) {
            fetchOverview();
        } else {
            setLoading(false);
        }
    }, [canManageSettings, fetchOverview]);

    const applyMfaState = async (targetEnabled) => {
        setIsDisableMfaModalOpen(false);
        setSavingMfa(true);
        setError('');
        setMessage('');

        try {
            const res = await apiClient('SecuritySettings/mfa', {
                method: 'POST',
                body: { enabled: targetEnabled }
            });
            setMfaEnabled(Boolean(res.mfaEnabled));
            setMessage(res.message || 'İki Adımlı Doğrulama (MFA) politikası güncellendi.');
            fetchOverview();
        } catch (err) {
            setError(err.payload?.message || err.message || 'MFA ayarı güncellenemedi.');
        } finally {
            setSavingMfa(false);
        }
    };

    const handleRequestMfaChange = (targetEnabled) => {
        if (savingMfa || targetEnabled === mfaEnabled) return;
        if (!targetEnabled) {
            setIsDisableMfaModalOpen(true);
            return;
        }
        applyMfaState(true);
    };

    const handleToggleRoleMfa = async (role) => {
        if (roleSavingId) return;
        const nextVal = !role.requiresMfa;
        setRoleSavingId(role.id);
        setError('');
        setMessage('');
        try {
            const res = await apiClient(`SecuritySettings/role-mfa/${role.id}`, {
                method: 'POST',
                body: { requiresMfa: nextVal }
            });
            setMessage(res?.message || `'${role.name}' rolü MFA zorunluluğu güncellendi.`);
            setOverview(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    roles: (prev.roles || []).map(r => r.id === role.id ? { ...r, requiresMfa: nextVal } : r)
                };
            });
        } catch (err) {
            setError(err.payload?.message || err.message || 'Rol MFA zorunluluğu güncellenemedi.');
        } finally {
            setRoleSavingId(null);
        }
    };

    const handleQuickMobilePolicyUpdate = async (key, boolValue) => {
        if (policySaving) return;
        setPolicySaving(true);
        setError('');
        setMessage('');
        try {
            const res = await mobileDeviceApi.updateSetting(key, boolValue ? "true" : "false");
            const deactivated = res?.deactivatedDeviceCount || 0;
            if (key === 'Mobile.AllowMultipleDevices' && !boolValue && deactivated > 0) {
                setMessage(`Tek Cihaz Politikası aktif edildi ve ${deactivated} adet eski cihaz otomatik olarak pasife alındı.`);
            } else {
                setMessage('Mobil güvenlik politikası başarıyla güncellendi.');
            }
            fetchOverview();
        } catch (err) {
            setError(err?.message || 'Mobil güvenlik politikası güncellenemedi.');
        } finally {
            setPolicySaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    if (!canManageSettings) {
        return (
            <div className="p-8">
                <div className="bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 p-6 rounded-2xl border border-rose-200 dark:border-rose-800/50 flex items-center gap-3 max-w-2xl mx-auto">
                    <AlertTriangle className="w-6 h-6 shrink-0" />
                    <p className="font-medium text-sm">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
                </div>
            </div>
        );
    }

    const userStats = overview?.userStats || { totalActiveUsers: 0, mfaEnabledUsersCount: 0, mustChangePasswordCount: 0, lockedUsersCount: 0 };
    const lockedUsers = overview?.lockedUsers || [];
    const roles = overview?.roles || [];
    const mobilePolicy = overview?.mobilePolicy || {};
    const auditStats24h = overview?.auditStats24h || { failedLoginsCount: 0, lockoutsCount: 0, successfulLoginsCount: 0 };
    const mfaRequiredRolesCount = roles.filter(r => r.requiresMfa).length;

    return (
        <div className="space-y-5">
            {/* Üst Başlık ve Canlı Güvenlik KPI Kartları */}
            <div className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-3.5">
                        <div className="w-11 h-11 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                            <Shield className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2.5 flex-wrap">
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    Firma Güvenlik Ayarları & Denetim Merkezi
                                </h2>
                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                                    mfaFeatureEnabled && mfaEnabled
                                        ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30'
                                        : 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30'
                                }`}>
                                    <SmartphoneNfc className="w-3.5 h-3.5" />
                                    {mfaFeatureEnabled && mfaEnabled ? '2FA (MFA) Aktif' : '2FA (MFA) Kapalı'}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                İki adımlı doğrulama (TOTP), rol bazlı MFA zorunluluğu, kilitlenen kullanıcı hesapları ve mobil cihaz güvenlik politikalarını tek merkezden yönetin.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={fetchOverview}
                            className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#111214] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            title="Güvenlik Verilerini Yenile"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => navigateTo('security_audit_logs')}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 border border-indigo-200/70 dark:border-indigo-500/30 transition-colors"
                        >
                            <Activity className="w-3.5 h-3.5" />
                            Güvenlik Loglarına Git
                        </button>
                    </div>
                </div>

                {/* 4'lü Güvenlik KPI Özeti */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mt-5">
                    <div className="p-3.5 rounded-xl bg-gray-50/80 dark:bg-[#111214] border border-gray-200/80 dark:border-gray-800">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>MFA Kullanan Personel</span>
                            <SmartphoneNfc className="w-4 h-4 text-emerald-500" />
                        </div>
                        <p className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                            {userStats.mfaEnabledUsersCount} <span className="text-xs font-normal text-gray-400">/ {userStats.totalActiveUsers} aktif kullanıcı</span>
                        </p>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            Şifre değişimi bekleyen: {userStats.mustChangePasswordCount}
                        </span>
                    </div>

                    <div className="p-3.5 rounded-xl bg-gray-50/80 dark:bg-[#111214] border border-gray-200/80 dark:border-gray-800">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>MFA Zorunlu Roller</span>
                            <KeyRound className="w-4 h-4 text-indigo-500" />
                        </div>
                        <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
                            {mfaRequiredRolesCount} <span className="text-xs font-normal text-gray-400">/ {roles.length} rol</span>
                        </p>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            Aşağıdaki tablodan rol bazlı değiştirebilirsiniz
                        </span>
                    </div>

                    <div className="p-3.5 rounded-xl bg-gray-50/80 dark:bg-[#111214] border border-gray-200/80 dark:border-gray-800">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Şu An Kilitli Hesaplar</span>
                            <Lock className={`w-4 h-4 ${userStats.lockedUsersCount > 0 ? 'text-rose-500' : 'text-emerald-500'}`} />
                        </div>
                        <p className={`text-xl font-bold mt-1 ${userStats.lockedUsersCount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                            {userStats.lockedUsersCount}
                        </p>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            Son 24 saatte kilitlenen: {auditStats24h.lockoutsCount}
                        </span>
                    </div>

                    <div className="p-3.5 rounded-xl bg-gray-50/80 dark:bg-[#111214] border border-gray-200/80 dark:border-gray-800">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span>Son 24 Saat Hatalı Giriş</span>
                            <ShieldAlert className="w-4 h-4 text-amber-500" />
                        </div>
                        <p className="text-xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                            {auditStats24h.failedLoginsCount}
                        </p>
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                            Başarılı giriş (24s): {auditStats24h.successfulLoginsCount}
                        </span>
                    </div>
                </div>
            </div>

            {/* Bildirimler */}
            {error && (
                <div className="p-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50 rounded-2xl flex items-center justify-between gap-3 text-rose-700 dark:text-rose-300 text-xs font-medium">
                    <div className="flex items-center gap-2.5">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>{error}</span>
                    </div>
                    <button type="button" onClick={() => setError('')} className="font-bold">✕</button>
                </div>
            )}
            {message && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 rounded-2xl flex items-center justify-between gap-3 text-emerald-700 dark:text-emerald-300 text-xs font-medium">
                    <div className="flex items-center gap-2.5">
                        <CheckCircle2 className="w-4 h-4 shrink-0" />
                        <span>{message}</span>
                    </div>
                    <button type="button" onClick={() => setMessage('')} className="font-bold">✕</button>
                </div>
            )}

            {/* Şu An Kilitli Hesaplar Varsa Acil Müdahale Paneli */}
            {lockedUsers.length > 0 && (
                <div className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-rose-200 dark:border-rose-900/50">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 flex items-center justify-center text-rose-600 dark:text-rose-400">
                                <Lock className="w-4 h-4" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                                    Şu An Kilitli Olan Kullanıcı Hesapları ({lockedUsers.length})
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Üst üste hatalı şifre denemesi nedeniyle geçici olarak kilitlenen personellerin kilidini buradan anında kaldırabilirsiniz.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {lockedUsers.map(u => (
                            <div
                                key={u.id}
                                className="flex items-center justify-between p-3.5 rounded-xl bg-rose-50/40 dark:bg-[#111214] border border-rose-200/70 dark:border-rose-900/40"
                            >
                                <div>
                                    <p className="text-xs font-bold text-gray-900 dark:text-white">
                                        {u.fullName}
                                        {u.username && u.username !== u.fullName && (
                                            <span className="ml-1.5 font-mono text-[11px] text-gray-500">({u.username})</span>
                                        )}
                                    </p>
                                    <p className="text-[11px] text-rose-600 dark:text-rose-400 mt-0.5">
                                        {u.accessFailedCount} hatalı deneme · Kilit bitişi: {u.lockoutEndAtStr} (~{u.remainingMinutes} dk kaldı)
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSelectedUserForUnlock({
                                        userId: u.id,
                                        targetUsername: u.username,
                                        targetFullName: u.fullName
                                    })}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors"
                                >
                                    <Unlock className="w-3.5 h-3.5" />
                                    Kilidi Aç
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 1. İKİ ADIMLI DOĞRULAMA (MFA) VE ROL BAZLI ZORUNLULUK */}
            <div className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 space-y-5">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200/70 dark:border-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                        <SmartphoneNfc className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-gray-900 dark:text-white">
                            İki Adımlı Doğrulama (MFA / TOTP) Yönetimi
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Google Authenticator veya Microsoft Authenticator ile 6 haneli zaman tabanlı tek kullanımlık şifre (TOTP) doğrulamasını yapılandırın.
                        </p>
                    </div>
                </div>

                {!mfaFeatureEnabled ? (
                    <div className="p-4 border border-amber-200 dark:border-amber-800/50 bg-amber-50/80 dark:bg-amber-950/20 rounded-xl flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                        <div className="text-xs">
                            <h4 className="font-bold text-amber-900 dark:text-amber-300">
                                İki Adımlı Doğrulama (MFA) Modülü Lisansınızda Aktif Değil
                            </h4>
                            <p className="text-amber-700 dark:text-amber-400 mt-1">
                                Firmanızda TOTP tabanlı iki adımlı doğrulamayı kullanabilmek için sistem yöneticiniz veya müşteri temsilciniz üzerinden MFA lisans özelliğini aktif ettirebilirsiniz.
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800">
                            <div>
                                <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                                    Firma Genelinde MFA Kullanımına İzin Ver
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-2xl leading-relaxed">
                                    Bu ayar açık olduğunda personeller kendi profil sayfalarından QR kod okutarak MFA kurabilir ve aşağıdaki roller için MFA zorunlu tutulabilir. Kapatıldığında tüm roller geçici olarak tek adımlı (şifre ile) girişe döner.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-1.5 bg-gray-200/80 dark:bg-[#18191c] p-1 rounded-xl border border-gray-200 dark:border-gray-800 w-full md:w-60 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => handleRequestMfaChange(false)}
                                    disabled={savingMfa}
                                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                        !mfaEnabled
                                            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm border border-gray-200/80 dark:border-gray-700'
                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                    }`}
                                >
                                    Kapalı
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleRequestMfaChange(true)}
                                    disabled={savingMfa}
                                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                        mfaEnabled
                                            ? 'bg-emerald-600 text-white shadow-sm'
                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                    }`}
                                >
                                    Açık (Aktif)
                                </button>
                            </div>
                        </div>

                        {/* Rol Bazlı MFA Zorunluluğu Tablosu */}
                        {roles.length > 0 && (
                            <div className="p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800 space-y-3">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <div>
                                        <h4 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                            <Users className="w-4 h-4 text-indigo-500" />
                                            Rol Bazlı MFA Zorunluluğu
                                        </h4>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            MFA zorunlu kılınan rollerdeki kullanıcılar giriş yaptıklarında iki adımlı doğrulama kurmadan sistemi kullanamazlar.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigateTo('role_management')}
                                        className="self-start sm:self-auto inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                                    >
                                        Rol Yönetimine Git <ArrowRight className="w-3.5 h-3.5" />
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 pt-1">
                                    {roles.map(role => (
                                        <div
                                            key={role.id}
                                            className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-[#18191c] border border-gray-200/80 dark:border-gray-800"
                                        >
                                            <div className="min-w-0 pr-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-gray-900 dark:text-white truncate">
                                                        {role.name}
                                                    </span>
                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                                                        {role.activeUserCount} kullanıcı
                                                    </span>
                                                </div>
                                                {role.description && (
                                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                                        {role.description}
                                                    </p>
                                                )}
                                            </div>

                                            <button
                                                type="button"
                                                disabled={!mfaEnabled || roleSavingId === role.id}
                                                onClick={() => handleToggleRoleMfa(role)}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all shrink-0 ${
                                                    role.requiresMfa
                                                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                                                        : 'bg-gray-100 dark:bg-[#111214] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-400'
                                                } ${!mfaEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                {roleSavingId === role.id
                                                    ? 'Kaydediliyor...'
                                                    : role.requiresMfa
                                                        ? 'MFA Zorunlu'
                                                        : 'Opsiyonel'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* 2. MOBİL CİHAZ VE OTURUM GÜVENLİĞİ HIZLI KONTROL */}
            <div className="bg-white dark:bg-[#18191c] p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/70 dark:border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                            <Smartphone className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                Mobil Cihaz & Oturum Güvenlik Politikaları
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Kayıtlı mobil cihazlar ({mobilePolicy.approvedDevicesCount ?? 0} onaylı, {mobilePolicy.pendingDevicesCount ?? 0} onay bekleyen, {mobilePolicy.blockedDevicesCount ?? 0} engelli) için aktif güvenlik kuralları.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => navigateTo('settings')}
                        className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-100 dark:bg-[#111214] text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors"
                    >
                        Tüm Cihazları Yönet <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Yeni Cihaz Onay Politikası */}
                    <div className="p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800 flex flex-col justify-between gap-4">
                        <div>
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-bold text-gray-900 dark:text-white">Yeni Cihaz Onay Politikası</h4>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                    mobilePolicy.autoApproveFirstDevice
                                        ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                        : 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300'
                                }`}>
                                    {mobilePolicy.autoApproveFirstDevice ? 'İlk Cihaz Otomatik' : 'Yönetici Onaylı'}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                                {mobilePolicy.autoApproveFirstDevice
                                    ? 'İlk kez giriş yapan personelin ilk telefonu otomatik onaylanır; sonraki cihazlar için kod/onay gerekir.'
                                    : 'Her yeni mobil cihaz girişinde yönetici onayı veya 6 haneli eşleştirme kodu zorunludur.'}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-1.5 bg-gray-200/80 dark:bg-[#18191c] p-1 rounded-xl border border-gray-200 dark:border-gray-800">
                            <button
                                type="button"
                                disabled={policySaving}
                                onClick={() => handleQuickMobilePolicyUpdate('Mobile.AutoApproveFirstDevice', false)}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                    !mobilePolicy.autoApproveFirstDevice
                                        ? 'bg-amber-500 text-white shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400'
                                }`}
                            >
                                Her Cihaza Kod Sor
                            </button>
                            <button
                                type="button"
                                disabled={policySaving}
                                onClick={() => handleQuickMobilePolicyUpdate('Mobile.AutoApproveFirstDevice', true)}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                    mobilePolicy.autoApproveFirstDevice
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400'
                                }`}
                            >
                                İlk Girişi Onayla
                            </button>
                        </div>
                    </div>

                    {/* Çoklu Cihaz Politikası */}
                    <div className="p-4 bg-gray-50/80 dark:bg-[#111214] rounded-xl border border-gray-200/80 dark:border-gray-800 flex flex-col justify-between gap-4">
                        <div>
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                                    <Layers className="w-4 h-4 text-indigo-500" />
                                    Çoklu Cihaz Politikası
                                </h4>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                    mobilePolicy.allowMultipleDevices
                                        ? 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                                        : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                }`}>
                                    {mobilePolicy.allowMultipleDevices ? 'Çoklu Cihaz Açık' : 'Tek Cihaz Koruması'}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                                {mobilePolicy.allowMultipleDevices
                                    ? 'Personeller onaylanmış birden fazla mobil cihazda aynı anda oturum açabilir.'
                                    : 'Personel yalnızca tek bir aktif cihaz kullanabilir; yeni cihaz onaylandığında eski cihaz otomatik düşürülür.'}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-1.5 bg-gray-200/80 dark:bg-[#18191c] p-1 rounded-xl border border-gray-200 dark:border-gray-800">
                            <button
                                type="button"
                                disabled={policySaving}
                                onClick={() => handleQuickMobilePolicyUpdate('Mobile.AllowMultipleDevices', false)}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                    !mobilePolicy.allowMultipleDevices
                                        ? 'bg-emerald-600 text-white shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400'
                                }`}
                            >
                                Sadece Tek Cihaz
                            </button>
                            <button
                                type="button"
                                disabled={policySaving}
                                onClick={() => handleQuickMobilePolicyUpdate('Mobile.AllowMultipleDevices', true)}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                    mobilePolicy.allowMultipleDevices
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400'
                                }`}
                            >
                                Çoklu Cihaza İzin Ver
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* MFA Kapatma Onay Modalı */}
            <InfoModal
                isOpen={isDisableMfaModalOpen}
                onClose={() => setIsDisableMfaModalOpen(false)}
                title="İki Adımlı Doğrulamayı (MFA) Devre Dışı Bırak"
                message="Firma genelinde İki Adımlı Doğrulamayı (MFA) kapatmak istediğinize emin misiniz? Bu işlem sonrasında 'MFA Zorunlu' olan roller dahil tüm kullanıcılar yalnızca şifre ile giriş yapabilecektir."
                type="confirm"
                onConfirm={() => applyMfaState(false)}
                confirmText="Evet, MFA'yı Kapat"
                cancelText="Vazgeç"
            />

            {/* Kilit Açma Modalı */}
            {selectedUserForUnlock && (
                <UnlockConfirmModal
                    user={selectedUserForUnlock}
                    onClose={() => setSelectedUserForUnlock(null)}
                    onSuccess={() => {
                        setSelectedUserForUnlock(null);
                        setMessage('Kullanıcının hesap kilidi başarıyla kaldırıldı.');
                        fetchOverview();
                    }}
                />
            )}
        </div>
    );
}
